// @flow
import React, { Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container } from '@finect/tabular-core/Container';
import { ContainerInner } from '@finect/tabular-core/ContainerInner';
import { MainContent } from '@finect/tabular-core/MainContent';
import { H2, H4 } from '@finect/tabular-core/Headings';
import { Button } from '@finect/tabular-core/Button';
import { Heading } from '@finect/tabular-core/Heading';
import { SolidHero } from '@finect/tabular-components/Hero';
import { Datalayer } from '@finect/front-gtm';
import imported from 'react-imported-component';
import type { ProfessionalType } from '../../types';
import Paragraph from './partials/Paragraph';
import Block from './partials/Block';
import imgHero from './partials/img/hero-advisors-min.png';

const Section = imported(() =>
  import(/* webpackChunkName: "TC-Section" */ '@finect/tabular-components/Section').then(module => module.Section));

type Props = {
  staticContext?: Object,
  history: Object,
  location: Object,
  toggleModal: Function
};

type State = {
  professionals: Array<ProfessionalType>
};

export class Advisor extends React.Component<Props, State> {

  state =
    typeof window === 'undefined'
      ? {
        professionals: this.props.staticContext ? this.props.staticContext.initialData.professionals : []
      }
      : {
        professionals: (window.initialData && window.initialData.professionals) || []
      };

  redirectTo = (url: string) => {
    this.props.history.push(url);
  };

  startOnboarding = () => {
    document.location.href =
      // eslint-disable-next-line max-len
      'https://login.finect.com?next=https://www.finect.com/usuario/configuracion/request';
  };

  // static loadInitialData() {
  // }

  render() {
    return (
      <Fragment>
        <Helmet>
          <title>Asesores financieros - Finect Asesores</title>
          <meta name="description" content="Aprovecha al máximo Finect Asesores" />
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href={`https://${process.env.FINECT_BASEURL || 'www.finect.com'}/asesores/asesor`} />
        </Helmet>

        <Datalayer id="Scene" type="ready" params={{ pageType: 'advisersProfessionals' }} />
        <Datalayer id="Scene" type="pageType" params={{ pageType: 'advisersProfessionals' }} />

        <SolidHero image={imgHero}>
          <Heading as="h1" size="h2">
            Aprovecha al máximo Finect Asesores
          </Heading>

          <Paragraph size="xlarge">
            {`Miles de ahorradores acuden diariamente a Finect para tomar sus
                decisiones financieras. Queremos asegurarnos de que conoces
                todas las alternativas para explotar al máximo las posibilidades
                que ofrece la plataforma de llegar a más clientes potenciales y
                visibilizar tu marca.`}
          </Paragraph>

          <Button
            secondary
            width="auto"
            as="a"
            href={`https://login.finect.com?next=https://www.finect.com/usuario/configuracion/request`}
          >
            Empieza ahora
          </Button>
        </SolidHero>

        <Container>
          <ContainerInner>
            <MainContent>
              <Section bottomMargin>
                <Block reverseOnPhone>
                  <div className="left text">
                    <H2>Tu perfil estará en nuestro listado de asesores</H2>
                    <p>
                      Los usuarios que estén buscando un asesor tendrán acceso a tu perfil. Diferénciate del resto:
                      tendrás más visibilidad cuanto más participes en la plataforma. Te podrán buscar por provincia o
                      por especialidad.
                    </p>
                    <br />
                    <Button variant="medium" width="auto" onClick={() => this.startOnboarding()}>
                      Empezar
                    </Button>
                  </div>
                  <div className="right image">
                    <img src="https://www.finect.com/image/35ca4d29bce8ccf68e760e936d18aefc3dcfdb36a30"></img>
                  </div>
                </Block>
              </Section>
              <Section topMargin bottomMargin>
                <Block>
                  <div className="left image">
                    <img src="https://www.finect.com/image/1ad5d6a8a62cd13534e10ff082ef3a5f2efb1e469ef"></img>
                  </div>
                  <div className="right text">
                    <H2>Recibe leads de usuarios que busquen un asesor</H2>
                    <p>
                      Te haremos llegar automáticamente las solicitudes de usuarios que estén buscando un asesor. Al
                      usuario le presentaremos un máximo de tres opciones, por lo que tus participaciones en Finect y tu
                      agilidad en el servicio serán muy relevantes.
                    </p>
                  </div>
                </Block>
              </Section>
              <Section topMargin bottomMargin>
                <Block reverseOnPhone>
                  <div className="left text">
                    <H2>Responde a preguntas para destacar como asesor</H2>
                    <p>
                      Llega a más clientes potenciales respondiendo a sus dudas en la sección de preguntas. Tu respuesta
                      aparecerá destacada como Respuesta Profesional y el usuario podrá acceder a tu perfil de asesor y
                      enviarte un mensaje.{' '}
                    </p>
                  </div>
                  <div className="right image">
                    <img src="https://www.finect.com/image/295ccf309e9612dd0b1b007acd7708b029a6d5e6b80"></img>
                  </div>
                </Block>
              </Section>
              <Section topMargin bottomMargin>
                <Block>
                  <div className="left image">
                    <img src="https://www.finect.com/image/3ce6daaf85814db695b6bbea3afa51f719e9d0f8bd7"></img>
                  </div>
                  <div className="right text">
                    <H2>Escribe reviews de producto para que se conozca tu experiencia</H2>
                    <p>
                      Cada día miles de inversores visitan las fichas de producto de Finect. Como asesor, puedes
                      escribir una opinión o review sobre cualquier producto (fondo de inversión, plan de pensiones,
                      depósito, seguro…). Tu review aparecerá destacada, ganarás exposición y te{' '}
                      <u>diferenciarás del resto</u>.
                    </p>
                  </div>
                </Block>
              </Section>
              <Section topMargin bottomMargin>
                <Block reverseOnPhone>
                  <div className="left text">
                    <H2>Comunícate con tus clientes de una forma efectiva</H2>
                    <p>
                      Nuestros servicios te permitirán comunicarte agilmente con los usuarios que están decidiendo en
                      qué invertir. Nuestra mensajería te permitirá compartir productos, pedirle permiso al usuario para
                      ver{' '}
                      <b>
                        <u>sus datos</u>
                      </b>{' '}
                      y ayudarle a mejorar sus finanzas.
                    </p>
                    <br />
                    <p>
                      Estamos probando un sistema de{' '}
                      <b>
                        <u>videoconferencia</u>
                      </b>{' '}
                      que te permitirá hablar con posibles clientes, ayudarles a entender conceptos, y llevarles a las
                      páginas más relevantes en Finect.
                    </p>
                  </div>
                  <div className="right image">
                    <img src="https://www.finect.com/image/0a1197ec4b46e425bd91e05caaf28ba64c9a7a1f412"></img>
                  </div>
                </Block>
              </Section>
            </MainContent>
          </ContainerInner>
          <ContainerInner>
            <MainContent>
              <H2>Algunas preguntas básicas</H2>
              <br />
              <br />
              <br />
              <H4>¿Cuánto cuesta el servicio?</H4>
              <p>
                Por €100 euros al mes tendrás acceso a todos nuestros servicios, recibir leads, e invitar a tus clientes
                actuales.
              </p>
              <br />
              <br />
              <H4>¿Tiene algún compromiso de permanencia?</H4>
              <p>No. Podrás cancelar en cualquier momento y se hará efectivo al mes siguiente.</p>
              <br />
              <br />
              <H4>¿Cómo me doy de alta?</H4>
              <p>
                Tienes que ser usuario de Finect y luego solicitar tu alta como asesor, identificar la empresa de
                servicios de inversión desde la que proporcionas el asesoramiento y proporcionarnos una tarjeta de
                crédito para realizar los pagos mensuales.
              </p>
              <br />
              <Button variant="medium" width="auto" onClick={() => this.startOnboarding()}>
                Empezar
              </Button>
            </MainContent>
          </ContainerInner>
        </Container>
      </Fragment>
    );
  }
}
